import React, { useRef } from "react";
import { Plus } from "lucide-react";

import { Card, Label, Hint, TextArea, TextInput, Counter } from "./ui";
import { countVariables } from "../templateUtils";

const BodyCard = ({
  body,
  variableCount,
  onChange,
  maxLength = 1024,
  label = "Message body",
  hint,
}) => {
  const ref = useRef(null);

  const insertVariable = () => {
    const next = countVariables(body.text) + 1;
    const el = ref.current;
    const token = `{{${next}}}`;

    if (el && typeof el.selectionStart === "number") {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const text = body.text.slice(0, start) + token + body.text.slice(end);
      onChange({ text });
      requestAnimationFrame(() => {
        el.focus();
        el.selectionStart = el.selectionEnd = start + token.length;
      });
    } else {
      onChange({ text: `${body.text}${token}` });
    }
  };

  const setExample = (i, value) => {
    const examples = [...body.examples];
    examples[i] = value;
    onChange({ examples });
  };

  return (
    <Card className="space-y-4">
      <div>
        <Label required>
          {label}
          <span className="ml-2 font-normal">
            <Counter value={body.text.length} max={maxLength} />
          </span>
        </Label>
        <TextArea
          ref={ref}
          rows={7}
          value={body.text}
          maxLength={maxLength}
          onChange={(e) => onChange({ text: e.target.value })}
          placeholder={"Hi {{1}}, your order {{2}} is on its way and should arrive by {{3}}."}
        />
        <div className="mt-2 flex items-center justify-between">
          <button
            type="button"
            onClick={insertVariable}
            className="inline-flex items-center gap-1.5 rounded-lg border border-[#b9dff0] bg-[#eaf4fa] px-2.5 py-1.5 text-[12px] font-medium text-[#0f6392] transition hover:bg-[#d4e9f5]"
          >
            <Plus size={13} />
            Add variable
          </button>
          {hint ? <Hint>{hint}</Hint> : <span />}
        </div>
      </div>

      {variableCount > 0 && (
        <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3.5">
          <p className="mb-2 text-[12px] font-medium text-slate-600">
            Sample values
            <span className="ml-1 font-normal text-slate-400">
              — shown to Meta reviewers, one per variable
            </span>
          </p>
          <div className="space-y-2">
            {Array.from({ length: variableCount }).map((_, i) => (
              <div key={i} className="flex items-center gap-2.5">
                <span className="w-9 flex-shrink-0 rounded-md bg-[#d4e9f5] py-1 text-center text-[11px] font-semibold text-[#0f6392]">
                  {`{{${i + 1}}}`}
                </span>
                <TextInput
                  value={body.examples[i] || ""}
                  onChange={(e) => setExample(i, e.target.value)}
                  placeholder={`Example for {{${i + 1}}}`}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};

export default BodyCard;
